import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Renderer2,
  ViewChild,
  OnInit,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AppBreadcrumb } from '@app/layout/components/app.breadcrumb';
import { LayoutService } from '@app/layout/service/layout.service';
import { ExternalApp } from '@core/models/external-app.model';
import { ExternalAppsService } from '@core/services/external-apps/external-apps.service';
import { MenuItem } from 'primeng/api';
import { Dock } from 'primeng/dock';
import { Toast } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { filter, Subscription } from 'rxjs';
import { ClientFooterComponent } from '../client-footer/client-footer.component';
import { ClientSidebarComponent } from '../client-sidebar/client-sidebar.component';
import { ClientTopbarComponent } from '../client-topbar/client-topbar.component';
import { ClientConfigService } from '@app/layout/service/client-config/client-config.service';
import { AppConfigurator } from '@app/layout/components/app.configurator';
import { generateShades, mapShadesToObject } from '@core/functions/colors';
import { ClientConfig } from '@core/models/client.model';
import { Shades } from '@modules/demo/domain/models/demo.interface';
import { SiteMenuSettings } from '@core/models/site-menu-settings.model';
import { SiteMenuSettingsService } from '@core/services/site-menu-settings/site-menu-settings.service';
import { StatusBannerStripComponent } from '@shared/components/status-banner-strip/status-banner-strip.component';

@Component({
  selector: 'd2d-client-layout',
  imports: [
    CommonModule,
    RouterOutlet,
    Toast,
    Dock,
    TooltipModule,
    ClientSidebarComponent,
    ClientTopbarComponent,
    ClientFooterComponent,
    AppBreadcrumb,
    AppConfigurator,
    StatusBannerStripComponent,
  ],
  templateUrl: './client-layout.component.html',
})
export class ClientLayoutComponent implements OnInit, OnDestroy {
  private readonly clientConfigService = inject(ClientConfigService);
  public clientConfig = this.clientConfigService.getConfig();
  private readonly externalAppService = inject(ExternalAppsService);
  private readonly siteMenuSettingsService = inject(SiteMenuSettingsService);
  public layoutService = inject(LayoutService);
  public renderer = inject(Renderer2);
  public router = inject(Router);

  overlayMenuOpenSubscription: Subscription;

  menuCloseSubscription: Subscription;

  menuOutsideClickListener: any;

  menuScrollListener: any;

  menuSettings: SiteMenuSettings | null = null;

  items = computed<MenuItem[]>(() => this.loadExternalApps(this.externalAppService.externalApps()));

  speedDialItems: MenuItem[] = [];

  @ViewChild(ClientSidebarComponent) clientSidebar!: ClientSidebarComponent;

  @ViewChild(ClientTopbarComponent) clientTopBar!: ClientTopbarComponent;

  constructor() {
    if (this.clientConfig) {
      this.changeTheme(this.clientConfig);
    }

    this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
      if (!this.menuOutsideClickListener) {
        this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) =>
          this.outsideClickListener(event),
        );
      }
      if (
        (this.layoutService.isHorizontal() ||
          this.layoutService.isSlim() ||
          this.layoutService.isCompact()) &&
        !this.menuScrollListener
      ) {
        this.menuScrollListener = this.renderer.listen(
          this.clientSidebar.menuContainer.nativeElement,
          'scroll',
          (_event) => {
            if (this.layoutService.isDesktop()) {
              this.hideMenu();
            }
          },
        );
      }
      if (this.layoutService.layoutState().staticMenuMobileActive) {
        this.blockBodyScroll();
      }
    });

    this.menuCloseSubscription = this.layoutService.menuClose$.subscribe(() => {
      this.hideMenu();
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.hideMenu();

      const container = document.querySelector('.layout-content-wrapper');
      if (container) {
        (container as HTMLElement).scrollTop = 0;
      }
    });
  }

  ngOnInit(): void {
    this.loadMenuSettings();
    this.subscribeToSettingsChanges();
  }

  private loadMenuSettings(): void {
    // Get client ID from config, fallback to 0 (default D2D)
    const clientId = this.clientConfig?.client_id || 0;

    this.siteMenuSettingsService.getCachedSettings(clientId).subscribe({
      next: (settings) => {
        this.menuSettings = settings;
        this.setMenu();
      },
      error: (error) => {
        console.error('Error loading menu settings:', error);
        // Set menu anyway with empty settings
        this.setMenu();
      },
    });
  }

  private subscribeToSettingsChanges(): void {
    const clientId = this.clientConfig?.client_id || 0;

    // Listen for settings updates
    this.siteMenuSettingsService.getSettingsUpdates().subscribe({
      next: (update) => {
        // Only react if it's for this client
        if (update && update.clientId === clientId) {
          this.menuSettings = update.settings;
          this.setMenu();
        }
      },
    });
  }

  changeTheme(config: ClientConfig): void {
    // Sett el color primario en la configuración del cliente
    document.documentElement.style.setProperty('--p-primary', config.primary_color || '#B1802E');

    // Genera y aplica los tonos del color primario
    this.updateShades(this.generateShades(config.primary_color || '#B1802E'));

    // Establece el color de fondo de la barra lateral
    document.documentElement.style.setProperty(
      '--sidebar-bg-color',
      config.bg_color || 'linear-gradient(0deg, rgba(0, 25, 44, 1) 0%, rgba(0, 45, 74, 1) 100%)',
    );

    // Establece el color de los encabezados del menú
    document.documentElement.style.setProperty(
      '--d-menuitem-root-text-color',
      config.header_text_color || '#B1802E',
    );
  }

  updateShades(shades: Record<number, string> | Shades): void {
    Object.entries(shades).forEach(([shade, value]) => {
      document.documentElement.style.setProperty(`--p-primary-${shade}`, value);
    });
  }

  generateShades(color: string): Shades {
    return mapShadesToObject(generateShades(color));
  }

  setMenu() {
    // Build menu items dynamically from settings
    const items: MenuItem[] = [];

    // WhatsApp
    if (this.menuSettings?.whatsapp_number) {
      items.push({
        label: 'WhatsApp',
        icon: 'pi pi-whatsapp',
        command: () => {
          const clean = this.menuSettings!.whatsapp_number!.replace(/\D/g, '');
          window.open(`https://wa.me/${clean}`, '_blank');
        },
      });
    }

    // Phone
    if (this.menuSettings?.customer_service_phone) {
      items.push({
        label: 'Teléfono',
        icon: 'pi pi-phone',
        command: () => {
          window.open(`tel:${this.menuSettings!.customer_service_phone}`, '_blank');
        },
      });
    }

    // Email
    if (this.menuSettings?.customer_service_email) {
      items.push({
        label: 'Email',
        icon: 'pi pi-envelope',
        command: () => {
          window.open(`mailto:${this.menuSettings!.customer_service_email}`, '_blank');
        },
      });
    }

    // Chatbot
    if (this.menuSettings?.chatbot_url) {
      items.push({
        label: 'Chatbot',
        icon: 'pi pi-comments',
        command: () => {
          window.open(this.menuSettings!.chatbot_url!, '_blank');
        },
      });
    }

    // Technical Service
    if (this.menuSettings?.technical_service_url) {
      items.push({
        label: 'Soporte técnico',
        icon: 'pi pi-cog',
        command: () => {
          window.open(this.menuSettings!.technical_service_url!, '_blank');
        },
      });
    }

    // Map/Location
    if (this.menuSettings?.map_url) {
      items.push({
        label: 'Ubicación',
        icon: 'pi pi-map-marker',
        command: () => {
          window.open(this.menuSettings!.map_url!, '_blank');
        },
      });
    }

    this.speedDialItems = items;
  }

  loadExternalApps(externalApps: ExternalApp[]) {
    const apps: MenuItem[] = [];

    externalApps.forEach((app) => {
      if (app.active && !!app.url?.trim()) {
        apps.push({
          label: app.name,
          icon: `./icons/socials/${app.icon}`,
          url: app.url,
          target: '_blank',
        });
      }
    });

    return apps;
  }

  outsideClickListener(event: any) {
    if (this.isOutsideClicked(event)) {
      this.hideMenu();
    }
  }

  isOutsideClicked(event: any) {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarButtonEl = document.querySelector('.topbar-left > a');

    return !(
      sidebarEl?.isSameNode(event.target) ||
      sidebarEl?.contains(event.target) ||
      topbarButtonEl?.isSameNode(event.target) ||
      topbarButtonEl?.contains(event.target)
    );
  }

  /** Escape cierra el menú, como en cualquier panel que se abre sobre el contenido. */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    const state = this.layoutService.layoutState();
    if (state.staticMenuMobileActive || state.overlayMenuActive) {
      this.hideMenu();
    }
  }

  hideMenu() {
    this.layoutService.layoutState.update((prev) => ({
      ...prev,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
      // Ver requestMenuClose: en móvil hay que apagar también sidebarActive o
      // el menú sigue visible aunque se cierre (tocarlo lo deja encendido).
      sidebarActive: this.layoutService.isMobile() ? false : prev.sidebarActive,
    }));
    if (this.layoutService.isHorizontal()) {
      this.layoutService.onMenuStateChange({ key: '' });
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }

    this.unblockBodyScroll();
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'),
        ' ',
      );
    }
  }

  containerClass = computed(() => {
    const layoutConfig = this.layoutService.layoutConfig();
    const layoutState = this.layoutService.layoutState();

    return {
      'layout-overlay': layoutConfig.menuMode === 'overlay',
      'layout-static': layoutConfig.menuMode === 'static',
      'layout-slim': layoutConfig.menuMode === 'slim',
      'layout-horizontal': layoutConfig.menuMode === 'horizontal',
      'layout-compact': layoutConfig.menuMode === 'compact',
      'layout-reveal': layoutConfig.menuMode === 'reveal',
      'layout-drawer': layoutConfig.menuMode === 'drawer',
      'layout-overlay-active': layoutState.overlayMenuActive || layoutState.staticMenuMobileActive,
      'layout-mobile-active': layoutState.staticMenuMobileActive,
      'layout-static-inactive':
        layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
      'layout-sidebar-active': layoutState.sidebarActive,
      'layout-sidebar-anchored': layoutState.anchored,
    };
  });

  ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuCloseSubscription) {
      this.menuCloseSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
  }
}
